import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { formValueSelector } from "redux-form";

import type { IPlaybookPage } from "@/app-utils/playbook.ts";
import { type Playbook } from "@/graphql.ts";
import { clearFormData, initPlaybookConfig } from "@/redux/modules/playbook-admin.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { ModulesIcons } from "@/settings/assets-settings.ts";
import {
  PLAYBOOK_CONFIGURATION_WIZARD_SCREENS,
  PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS,
  PLAYBOOK_TYPE,
} from "@/settings/settings.ts";
import PlaybookConfigP2WorkflowInputScreen from "@/ui-modules/playbooks/editor/PlaybookConfigP2WorkflowInputScreen.tsx";

import PlaybookWizard from "../PlaybookWizard.js";
import {
  PlaybookConfigBasicsInfoScreen,
  PlaybookConfigBasicsInfoScreenImpl,
} from "./PlaybookConfigBasicsInfoScreen.js";
import PlaybookConfigExperimentInfoScreen from "./PlaybookConfigExperimentInfoScreen.js";
import PlaybookConfigNfActivitiesConfigScreen from "./PlaybookConfigNfActivitiesConfigScreen.js";
import PlaybookConfigNfWorkflowInputScreen from "./PlaybookConfigNfWorkflowInputScreen.js";
import PlaybookConfigNgsResultsScreen from "./PlaybookConfigNgsResultsScreen.js";
import PlaybookConfigReportScreen from "./PlaybookConfigReportScreen.js";
import { PlaybookConfigSampleInfoScreen } from "./PlaybookConfigSampleInfoScreen.js";
import { PlaybookConfigSampleStatusScreen } from "./PlaybookConfigSampleStatusScreen.js";

interface IPlaybookConfigWizardOwnProps {
  initialPlaybookId?: string | number;
  onClose: () => void;
}

interface IPlaybookConfigWizardAppStateProps {
  hpcEnabled: boolean;
  cloudEnabled: boolean;
  documentationUrl: string;
  selectedPlaybookType: PLAYBOOK_TYPE;
}

interface IPlaybookConfigWizardDispatchProps {
  initPlaybookState: (playbookId: string | number) => Promise<Playbook>;
  clearFormData: () => void;
}

export interface IPlaybookConfigWizardProps
  extends IPlaybookConfigWizardAppStateProps,
    IPlaybookConfigWizardDispatchProps,
    IPlaybookConfigWizardOwnProps {}

function mapStateToProps(state: IAppState, _ownProps: IPlaybookConfigWizardProps): IPlaybookConfigWizardAppStateProps {
  return {
    hpcEnabled: state.config.configuration?.application?.hpcEnabled,
    cloudEnabled: state.config.configuration?.application?.cloudEnabled,
    documentationUrl: state.config.configuration?.application?.documentationUrl,
    selectedPlaybookType:
      formValueSelector(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME)(
        state,
        PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE,
      )?.value || state.playbookAdminState.playbookConfigState.playbookType,
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigWizardDispatchProps {
  return {
    initPlaybookState: (playbookId: number) => dispatch(initPlaybookConfig(playbookId)),
    clearFormData: () => dispatch(clearFormData()),
  };
}

interface IPlaybookConfigWizardState {
  initializationCompleted: boolean;
}

class PlaybookConfigWizardImpl extends Component<IPlaybookConfigWizardProps, IPlaybookConfigWizardState> {
  constructor(props: IPlaybookConfigWizardProps) {
    super(props);

    this.state = {
      initializationCompleted: false,
    };
  }

  componentDidMount() {
    if (this.props.initialPlaybookId) {
      this.props
        .initPlaybookState(this.props.initialPlaybookId)
        .then(() => this.setState({ initializationCompleted: true }));
    } else {
      this.setState({ initializationCompleted: true });
    }
  }

  onClose = () => {
    this.props.clearFormData();
    this.props.onClose();
  };

  render(): ReactElement {
    const steps: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS[] = [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.BASICS_INFO_SCREEN];
    switch (this.props.selectedPlaybookType) {
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
        steps.push(
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_STATUS_SCREEN,
        );
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
        steps.push(
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NGS_RESULTS_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.REPORT_SCREEN,
          PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_STATUS_SCREEN,
        );
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS:
      case PLAYBOOK_TYPE.RANGE_ANALYSIS:
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN);
        break;
      case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS:
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN);
        if (this.props.hpcEnabled || this.props.cloudEnabled) {
          steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_ACTIVITIES_CONFIG_SCREEN);
        }
        break;
      case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES:
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN);
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN);
        if (this.props.hpcEnabled || this.props.cloudEnabled) {
          steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_ACTIVITIES_CONFIG_SCREEN);
        }
        break;
      case PLAYBOOK_TYPE.REPORT_FROM_VARIANTS:
      case PLAYBOOK_TYPE.REPORT_FROM_COORDINATES:
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN);
        break;
      case PLAYBOOK_TYPE.PROCESSOR:
        // add second step for all P2 playbooks
        // (it contains the input config for Genome Viewer context and the parameter config applicable for both execution contexts)
        steps.push(PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.P2_EXPERIMENT_INFO_SCREEN);
        break;
    }

    return (
      <PlaybookWizard
        playbook
        title={"Playbook Editor"}
        className={"playbook-creation-wizard"}
        steps={steps.map((step: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS) => PLAYBOOK_CONFIGURATION_WIZARD_SCREENS[step])}
        mapStepToScreen={this.mapStepToScreen}
        onClose={this.onClose}
        isLoading={!this.state.initializationCompleted}
        icon={ModulesIcons.PLAYBOOK_EDITOR}
        helpLink={`${this.props.documentationUrl}Playbook%2bEditor`}
      />
    );
  }

  mapStepToScreen = (page: IPlaybookPage) => {
    switch (page?.pageIdentifier) {
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.BASICS_INFO_SCREEN:
        return <PlaybookConfigBasicsInfoScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN:
        return <PlaybookConfigExperimentInfoScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN:
        return <PlaybookConfigNfWorkflowInputScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.P2_EXPERIMENT_INFO_SCREEN:
        return <PlaybookConfigP2WorkflowInputScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_ACTIVITIES_CONFIG_SCREEN:
        return <PlaybookConfigNfActivitiesConfigScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN:
        return <PlaybookConfigSampleInfoScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NGS_RESULTS_SCREEN:
        return <PlaybookConfigNgsResultsScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.REPORT_SCREEN:
        return <PlaybookConfigReportScreen playbookPage={page} />;
      case PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_STATUS_SCREEN:
        return <PlaybookConfigSampleStatusScreen playbookPage={page} />;
    }
  };
}

const PlaybookConfigWizard = connect<
  IPlaybookConfigWizardAppStateProps,
  IPlaybookConfigWizardDispatchProps,
  IPlaybookConfigWizardOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(PlaybookConfigWizardImpl);

export { PlaybookConfigWizard };
export default PlaybookConfigWizard;
